import React, { useState, useEffect } from "react";
import { translate } from "react-i18next";
import { Container, QuestionContainer } from "./styles";
import Smile from "./Score/Smile";
import NPS from "./Score/NPS";
import Satisfaction from "./Score/Satisfaction";
import YesNo from "./Score/YesNo";
import Comment from "./Comment";
import Question from "./Question";
import Details from "./Details";
import api from "../../services/api";
import { captureException } from "@sentry/browser";
import Feedback from "./Feedback";

function SatisfactionSurvey({
  questions,
  t,
  lang,
  isMovideskContext = false,
  ticketInfo = {},
  guid,
  callback,
  renderedType,
  chatWidgetId
}) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questionsState, setQuestionsState] = useState(questions);
  const [question, setQuestion] = useState("");
  const [showDetails, setShowDetails] = useState(
    checkResponses() && !questionsState[0].commentaryEnabled
  );
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    const { languages } = questionsState[questionIndex];
    const q = languages.find(
      ({ language }) => language.substring(0, 2) === lang
    );
    if (q && q.description) {
      setQuestion(q.description);
    } else {
      setQuestion(languages[0].description);
    }
  }, [lang, questionIndex]);

  function questionWithoutResponse() {
    return questionsState.findIndex(question => !question.response);
  }

  function checkResponses() {
    return questionsState.every(el => !!el.response || el.response === 0);
  }

  function renderSurveyModel(model) {
    const props = {
      response: questionsState[questionIndex].response,
      onValueChange: handleValueChange,
      onClick: handleSubmitResponse,
      guid: guid,
      questionIndexToModel: questionIndex
    };

    switch (model) {
      case 1:
        return <Satisfaction {...props} />;
      case 2:
        return <Smile {...props} />;
      case 3:
        return <NPS {...props} />;
      case 4:
        return <YesNo {...props} />;
      default:
        return null;
    }
  }

  function handleValueChange(value) {
    const q = questionsState.map((question, index) =>
      index === questionIndex ? { ...question, ...value } : question
    );
    setQuestionsState(q);
  }

  function handleSubmitResponse(
    value = questionsState[questionIndex].response
  ) {
    const { id, comment, commentaryEnabled } = questionsState[questionIndex];

    !commentaryEnabled && handleNext(questionIndex);

    sendResponse(id, { response: value, comment: comment }, chatWidgetId);
  }

  function handleNext(index = questionIndex) {
    const nextIndex = index + 1;
    const withoutResponse = questionWithoutResponse();
    if (nextIndex < questions.length) {
      setQuestionIndex(nextIndex);
    } else {
      withoutResponse !== -1
        ? setQuestionIndex(withoutResponse)
        : setQuestionIndex(0);
    }
  }

  function handlePrevious(index) {
    const previousIndex = index - 1;

    if (previousIndex < 0) return;

    setQuestionIndex(previousIndex);
  }

  function handleEdit() {
    setQuestionIndex(0);
    setShowDetails(false);
  }

  function handleSubmit() {
    const { id, response, comment } = questionsState[questionIndex];

    sendResponse(id, { response, comment }, chatWidgetId);
    if (checkResponses()) {
      setShowDetails(true);
      renderedType === "chat" && callback.endChat();
    } else {
      handleNext();
    }
  }

  async function sendResponse(id, data, chatWidget) {
    try {
      renderedType === "ticket" && callback.incrementValidBlockTicket();
      const params = new URLSearchParams(window.location.search);

      let token = "";
      if (params.get("t")) {
        token = params.get("t");
      }

      if (!token.length) {
        token = decodeURIComponent(window.location.search.substr(1));
      }

      let url = "",
        body = "";
      if (isMovideskContext) {
        url = `/SaveResponse?id=${id}&chatWidgetId=${chatWidget}`;
        body = {
          response: {
            ...data,
            ticketId: ticketInfo.ticketId,
            ticketInfo: ticketInfo.clientId
          }
        };
      } else {
        url = `/${id}/response?token=${encodeURIComponent(token)}`;
        body = data;
      }

      await api.patch(url, body);
      setShowFeedback(true);
    } catch (error) {
      captureException(error);
    }
  }

  const { type, commentaryEnabled, comment, response } = questionsState[
    questionIndex
  ];

  const renderType = renderSurveyModel(type);

  return (
    <Container>
      {ticketInfo.isAgent || showDetails ? (
        <Details
          onEdit={handleEdit}
          isAgent={ticketInfo.isAgent}
          questions={questionsState}
          lang={lang}
        />
      ) : (
        <>
          {!isMovideskContext && (
            <div>
              <h3>
                {t("knowWhatYouThink")} {":)"}
              </h3>
              <h5>{t("withYourFeedbackWeCanImproveRelationship")}</h5>
            </div>
          )}
          <QuestionContainer>
            <Question question={question} />
            <Feedback show={showFeedback} setShow={setShowFeedback} />
          </QuestionContainer>
          {renderType}
          <Comment
            enabled={commentaryEnabled}
            comment={!comment ? "" : comment}
            index={questionIndex}
            length={questionsState.length}
            isMovideskContext={isMovideskContext}
            isntAnswered={response === 0 ? response : !response}
            onValueChange={handleValueChange}
            onNext={() => handleNext(questionIndex)}
            onPrevious={() => handlePrevious(questionIndex)}
            onSubmit={handleSubmit}
            onBlurCommentary={handleSubmitResponse}
          />
        </>
      )}
    </Container>
  );
}

export default translate("translations")(SatisfactionSurvey);
